import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { subscriptionService } from '../services/subscriptionService';

/**
 * Hook customizado para gerenciar assinaturas
 */
export const useSubscription = () => {
  const queryClient = useQueryClient();

  // Query para buscar assinatura atual
  const { data: currentSubscription, isLoading } = useQuery({
    queryKey: ['subscription', 'current'],
    queryFn: subscriptionService.getCurrent,
    staleTime: 5 * 60 * 1000, // 5 minutos
    retry: 1,
  });

  // Query para buscar histórico
  const { data: history, isLoading: isLoadingHistory } = useQuery({
    queryKey: ['subscription', 'history'],
    queryFn: subscriptionService.getHistory,
    staleTime: 5 * 60 * 1000,
    enabled: false, // Só busca quando solicitado
  });

  // Query para buscar histórico de pagamentos
  const { data: payments, isLoading: isLoadingPayments } = useQuery({
    queryKey: ['subscription', 'payments'],
    queryFn: subscriptionService.getPayments,
  });

  // Mutation para criar assinatura
  const subscribeMutation = useMutation({
    mutationFn: ({ planId, billingType }) => 
      subscriptionService.subscribe(planId, billingType),
    onSuccess: (data) => {
      queryClient.invalidateQueries(['subscription']);
      toast.success('Assinatura criada com sucesso!');

      // Redirecionar para URL de pagamento do Asaas
      if (data.data?.payment_url) {
        window.open(data.data.payment_url, '_blank');
      }
    },
    onError: (error) => {
      const errorMessage = error.response?.data?.error || 
                          error.response?.data?.message || 
                          'Erro ao criar assinatura';
      toast.error(errorMessage);
    }
  });

  // Mutation para fazer upgrade
  const upgradeMutation = useMutation({
    mutationFn: (planId) => subscriptionService.upgrade(planId),
    onSuccess: () => {
      queryClient.invalidateQueries(['subscription']);
      toast.success('Plano atualizado com sucesso!');
    },
    onError: (error) => {
      const errorMessage = error.response?.data?.error || 
                          error.response?.data?.message || 
                          'Erro ao atualizar plano';
      toast.error(errorMessage);
    }
  });

  // Mutation para cancelar assinatura
  const cancelMutation = useMutation({
    mutationFn: subscriptionService.cancel,
    onSuccess: () => {
      queryClient.invalidateQueries(['subscription']);
      toast.success('Assinatura cancelada com sucesso');
    },
    onError: (error) => {
      const errorMessage = error.response?.data?.error || 
                          error.response?.data?.message || 
                          'Erro ao cancelar assinatura';
      toast.error(errorMessage);
    }
  });

  return {
    // Dados
    currentSubscription: currentSubscription?.data,
    history: history?.data,
    payments: payments?.data,
    
    // Estados de loading
    isLoading,
    isLoadingHistory,
    isLoadingPayments,
    
    // Mutations
    subscribe: subscribeMutation.mutate,
    upgrade: upgradeMutation.mutate,
    cancel: cancelMutation.mutate,
    
    // Estados das mutations
    isSubscribing: subscribeMutation.isPending,
    isUpgrading: upgradeMutation.isPending,
    isCancelling: cancelMutation.isPending,
    
    // Funções auxiliares
    refetch: () => queryClient.invalidateQueries(['subscription']),
  };
};
